// ============================================================================
// NOTICE RETURN STATUS HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { noticeReturnStatusService } from '../../api/services/noticeReturnStatusService';
import type {
  NoticeReturnStatus,
  NoticeReturnStatusListItem,
  CreateNoticeReturnStatusRequest,
  UpdateNoticeReturnStatusRequest,
  NoticeReturnStatusSearchRequest,
} from '../../api/types/noticeReturnStatus.types';
import type { ID } from '../../api/types';

export const useNoticeReturnStatuses = (params?: NoticeReturnStatusSearchRequest) => {
  const [data, setData] = useState<NoticeReturnStatusListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await noticeReturnStatusService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return { data, loading, error, pagination, refetch: fetchItems };
};

export const useNoticeReturnStatus = (id: ID | undefined) => {
  const [data, setData] = useState<NoticeReturnStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchItem = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await noticeReturnStatusService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  return { data, loading, error, refetch: fetchItem };
};

export const useCreateNoticeReturnStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createItem = async (
    data: CreateNoticeReturnStatusRequest,
    options?: { onSuccess?: (item: NoticeReturnStatus) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await noticeReturnStatusService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createNoticeReturnStatus: createItem, loading, error };
};

export const useUpdateNoticeReturnStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateItem = async (
    id: ID, data: UpdateNoticeReturnStatusRequest,
    options?: { onSuccess?: (item: NoticeReturnStatus) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await noticeReturnStatusService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateNoticeReturnStatus: updateItem, loading, error };
};

export const useDeleteNoticeReturnStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteItem = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await noticeReturnStatusService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteNoticeReturnStatus: deleteItem, loading, error };
};
